import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================
// UPDATED: Request interceptor for JWT token
// ============================================
api.interceptors.request.use(
  (config) => {
    // Add JWT token to Authorization header
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // BusinessId is now in JWT token, but we can still add header for backward compatibility
    const businessId = localStorage.getItem('businessId');
    if (businessId) {
      config.headers['X-Business-Id'] = businessId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================
// UPDATED: Response interceptor
// ============================================
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't auto-redirect on 401 if it's the login endpoint
    // Let the login flow handle it properly (trial expiration, wrong password, etc.)
    const isLoginEndpoint = error.config?.url?.includes('/auth/login');
    const isRegisterEndpoint = error.config?.url?.includes('/auth/register');
    
    if (error.response?.status === 401 && !isLoginEndpoint && !isRegisterEndpoint) {
      // Token expired or invalid - clear everything and redirect
      // (but NOT if it's a login/register failure)
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('businessId');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// Your existing API methods (keep as-is)
// ============================================
export const customersApi = {
  getAll: (includeInactive = false) => api.get(`/customers?includeInactive=${includeInactive}`),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  deactivate: (id) => api.post(`/customers/${id}/deactivate`),
  reactivate: (id) => api.post(`/customers/${id}/reactivate`),
  search: (query) => api.get(`/customers/search?query=${encodeURIComponent(query)}`),
  sendPortalAccess: async (id) => {
    return await api.post(`/customers/${id}/send-portal-access`);
  },
  getPricingHistory: (id) => api.get(`/customers/${id}/pricing-history`),
};

export const customerAssetsApi = {
  // Get all assets for a customer
  getByCustomer: (customerId, options = {}) => {
    const params = new URLSearchParams();
    if (options.includeInactive) params.append('includeInactive', 'true');
    if (options.assetType) params.append('assetType', options.assetType);
    const queryString = params.toString();
    return api.get(`/customers/${customerId}/assets${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get single asset
  get: (customerId, assetId) => api.get(`/customers/${customerId}/assets/${assetId}`),
  
  // Create new asset
  create: (customerId, data) => api.post(`/customers/${customerId}/assets`, data),
  
  // Update asset
  update: (customerId, assetId, data) => api.put(`/customers/${customerId}/assets/${assetId}`, data),
  
  // Delete asset
  delete: (customerId, assetId) => api.delete(`/customers/${customerId}/assets/${assetId}`),
  
  // Set as primary
  setPrimary: (customerId, assetId) => api.post(`/customers/${customerId}/assets/${assetId}/set-primary`),
  
  // Get by type
  getByType: (customerId, assetType) => api.get(`/customers/${customerId}/assets/by-type/${assetType}`),
};

export const jobsApi = {
  getAll: () => api.get('/jobs'),
  getById: (id) => api.get(`/jobs/${id}`),
  getByCustomer: (customerId) => api.get(`/jobs/customer/${customerId}`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  updateStatus: (id, status) => api.patch(`/jobs/${id}/status`, { status }),
  sendPrepEmail: (id) => api.post(`/jobs/${id}/send-prep-email`),
  sendQuoteEmail: (id) => api.post(`/jobs/${id}/send-quote-email`),
  sendQuoteSms: (id) => api.post(`/jobs/${id}/send-quote-sms`),
  // Manual review request (owner-initiated; nothing sends automatically)
  getReviewRequest: (id) => api.get(`/jobs/${id}/review-request`),
  markReviewRequested: (id) => api.post(`/jobs/${id}/review-requested`),
  clearReviewRequested: (id) => api.delete(`/jobs/${id}/review-requested`),
  /**
   * Complete a job with optional on-site payment
   * If paidOnSite is true, creates invoice and payment in one transaction
   * @param {number} id - Job ID
   * @param {Object} data - Completion data
   * @param {number} data.finalAmount - Final amount charged
   * @param {string} data.completedDate - Completion date (ISO string)
   * @param {string} data.notes - Completion notes
   * @param {boolean} data.sendCompletedEmail - Send completion notification
   * @param {boolean} data.paidOnSite - Customer paid on site
   * @param {string} data.paymentMethod - Payment method (Cash, Check, etc.)
   * @param {string} data.paymentReference - Reference number
   * @param {boolean} data.sendReceiptEmail - Send payment receipt
   */
  complete: (id, data) => api.post(`/jobs/${id}/complete`, data),
};

export const invoicesApi = {
  getAll: () => api.get('/invoices'),
  getById: (id) => api.get(`/invoices/${id}`),
  getByJob: (jobId) => api.get(`/invoices/job/${jobId}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  markAsPaid: (id) => api.patch(`/invoices/${id}/paid`),
  addPayment: (invoiceId, paymentData) => api.post(`/invoices/${invoiceId}/payments`, paymentData),
  send: (invoiceId) => api.post(`/invoices/${invoiceId}/send`),
  sendSms: (invoiceId) => api.post(`/invoices/${invoiceId}/send-sms`),
  getOverdue: () => api.get('/invoices/overdue'),
  getByCustomer: (customerId) => api.get(`/invoices/customer/${customerId}`),
};

export const paymentsApi = {
  // Optional params: { method, from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
  getAll: (params) => api.get('/payments', { params }),
};

export const expensesApi = {
  getAll: (params) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getSummary: (params) => api.get('/expenses/summary', { params }),
  
  // ============================================
  // NEW: SuddySnap™ AI Receipt Scanner
  // ============================================
  scanReceipt: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/expenses/scan-receipt', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Increase timeout for OCR processing (can take a few seconds)
      timeout: 30000,
    });
  },
};

export const expenseCategoriesApi = {
  getGrouped: () => api.get('/expense-categories'),
  getFlat: () => api.get('/expense-categories/flat'),
};

export const serviceTypesApi = {
  getAll: () => api.get('/servicetypes'),
  getById: (id) => api.get(`/servicetypes/${id}`),
  create: (data) => api.post('/servicetypes', data),
  update: (id, data) => api.put(`/servicetypes/${id}`, data),
  delete: (id) => api.delete(`/servicetypes/${id}`),
};

export const taxConfigApi = {
  // Get tax setup status
  getStatus: () => api.get('/tax-configuration/status'),
  
  // Get current configuration
  getConfiguration: () => api.get('/tax-configuration'),
  
  // Save configuration
  saveConfiguration: (config) => api.post('/tax-configuration', config),
  
  // Dismiss setup wizard
  dismiss: () => api.post('/tax-configuration/dismiss'),
  
  // Reset dismissal (show wizard again)
  resetDismissal: () => api.post('/tax-configuration/reset-dismissal'),
};

// ============================================
// TAX RATES API (Used for rate lookups)
// ============================================
export const taxRatesApi = {
  // Get counties for a state (for dropdown)
  getCounties: (state) => api.get(`/tax-rates/counties?state=${state}`),
  
  // Lookup sales tax rate for state/county
  lookup: (state, county, city) => {
    let url = `/tax-rates/lookup?state=${state}`;
    if (county) url += `&county=${encodeURIComponent(county)}`;
    if (city) url += `&city=${encodeURIComponent(city)}`;
    return api.get(url);
  },
  
  // Get states that have rate data
  getStatesWithRates: () => api.get('/tax-rates/states'),
  
  // Get state income tax rate
  getStateIncomeTax: (state, year) => {
    let url = `/tax-rates/state-income?state=${state}`;
    if (year) url += `&year=${year}`;
    return api.get(url);
  },
  
  // Get federal tax rates (SE tax, SS wage base, etc.)
  getFederalRates: (year) => {
    let url = '/tax-rates/federal';
    if (year) url += `?year=${year}`;
    return api.get(url);
  },
};

export const reportsApi = {
  getProfitLoss: (params) => api.get('/reports/profit-loss', { params }),
  getScheduleC: (params) => api.get('/reports/schedule-c', { params }),
  getQuarterlyTax: (params) => api.get('/reports/quarterly-tax', { params }),
  getTaxReminders: () => api.get('/reports/tax-reminders'),
  
  // NEW: Enhanced reports with "wow" insights
  getAnalytics: (params) => api.get('/reports/analytics', { params }),
  getHealthScorecard: () => api.get('/reports/health-scorecard'),
  getCustomerIntelligence: () => api.get('/reports/customer-intelligence'),

  // CSV Exports - Updated with excludePreBusiness support
  exportExpensesCsv: (year, excludePreBusiness = true) => 
    api.get(`/reports/expenses/csv?year=${year}&excludePreBusiness=${excludePreBusiness}`, { responseType: 'blob' }),
  
  exportRevenueCsv: (year) => 
    api.get(`/reports/revenue/csv?year=${year}`, { responseType: 'blob' }),
  
  exportScheduleCCsv: (year, excludePreBusiness = true) => 
    api.get(`/reports/schedule-c/csv?year=${year}&excludePreBusiness=${excludePreBusiness}`, { responseType: 'blob' }),
  
  // Tax Package (ZIP with everything) - Updated with excludePreBusiness support
  downloadTaxPackage: (year, excludePreBusiness = true) => 
    api.get(`/reports/tax-package?year=${year}&excludePreBusiness=${excludePreBusiness}`, { responseType: 'blob' }),
};

export const leadsApi = {
  getAll: (status = null) => {
    const url = status ? `/leads?status=${status}` : '/leads';
    return api.get(url);
  },
  getById: (id) => api.get(`/leads/${id}`),  
  create: (leadData) => api.post('/leads', leadData),  
  update: (id, leadData) => api.put(`/leads/${id}`, leadData),  
  delete: (id) => api.delete(`/leads/${id}`),  
  convertToCustomer: (id) => api.post(`/leads/${id}/convert`),
  
  setFollowUpDate: (id, followUpDate) => api.patch(`/leads/${id}/follow-up`, { followUpDate }),
  updateStatus: (id, status, lostReason = null) => api.patch(`/leads/${id}/status`, { status, lostReason }),
};

// ============================================
// JobServices API methods
// ============================================
export const jobServicesApi = {
  // Get all services for a job
  getByJob: (jobId) => api.get(`/jobservices/job/${jobId}`),
  
  // Add a service to a job
  create: (data) => api.post('/jobservices', data),
  
  // Update a job service
  update: (id, data) => api.put(`/jobservices/${id}`, data),
  
  // Delete a job service
  delete: (id) => api.delete(`/jobservices/${id}`),
  
  // Replace all services for a job (bulk update)
  setJobServices: (jobId, data) => api.post(`/jobservices/job/${jobId}/set`, data),
  
  // Legacy: Set primary service (backward compatibility)
  setPrimary: (data) => api.post('/jobservices/set-primary', data),
};

// ============================================
// NEW: Auth API methods
// ============================================
export const authApi = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  checkEmail: (email) => api.get(`/auth/check-email/${email}`),
};

// ============================================
// Contact API methods (public, no auth required)
// ============================================
export const contactApi = {
  submit: (formData) => {
    // Create a separate axios instance without auth for contact form
    return axios.post(`${API_BASE_URL}/contact`, formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
};

// ============================================
// Early Access API methods (public, no auth required)
// ============================================
export const earlyAccessApi = {
  submit: (email) => {
    // Create a separate axios instance without auth for early access form
    return axios.post(`${API_BASE_URL}/earlyaccess`, { email }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
};

export const brandingApi = {
  // Get current branding settings
  getBranding: () => api.get('/business/branding'),
  
  // Update branding (brand color only)
  updateBranding: (data) => api.put('/business/branding', data),
  
  // Upload logo
  uploadLogo: (file, isAlt = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/business/logo?isAlt=${isAlt}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Delete logo
  deleteLogo: (isAlt = false) => api.delete(`/business/logo?isAlt=${isAlt}`),
};

export default api;